import {useEffect, useRef, useState} from 'react';
import {useInView} from '../../../../../hooks/useInView/useInView';

function hasAudio(
  video: HTMLVideoElement & {
    mozHasAudio?: boolean;
    webkitAudioDecodedByteCount?: number;
    audioTracks?: {length: number};
  },
) {
  // This checks for Firefox, Chrome and Safari whether the video has audio (displaying the custom-mute/unmute control depend on it)
  return (
    video.mozHasAudio ||
    Boolean(video.webkitAudioDecodedByteCount) ||
    Boolean(video.audioTracks && video.audioTracks.length)
  );
}

export const useVideoControls = (intersectionThreshold?: number) => {
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  // Since this component automatically plays the video when it is in view
  // we need a way to prevent this functionality if there are custom controls rendered and
  // the user interacts with them. We are keeping the auto-pause function in order to prevent
  // bad performance on low-performance devices. But as soon as the override has happened, auto-play
  // when the video is back in view won't happen anymore until refresh.
  const isUserPlayPauseOverrideActive = useRef(false);

  const {ref: videoRef, inView} = useInView<HTMLVideoElement>({
    threshold: intersectionThreshold ?? 0.5,
  });

  const handlePlayToggleClick = () => {
    isUserPlayPauseOverrideActive.current = true;
    if (!playing && videoRef.current) {
      videoRef.current.play()?.catch(() => {
        // No need to handle the error here, it will be handled by the video element itself
      });
      setPlaying(true);
    }
    if (playing && videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const handleMuteToggleClick = () => {
    setMuted(!muted);
  };

  // eslint-disable-next-line local-rules/prefer-use-inview-effect -- we cannot use useInViewEffect here because we need the inView value in the return value of this hook
  useEffect(() => {
    if (
      !isUserPlayPauseOverrideActive.current &&
      inView &&
      !playing &&
      videoRef.current
    ) {
      videoRef.current.play();
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setPlaying(true);
    }
    if (!inView && playing && videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
    }
  }, [isUserPlayPauseOverrideActive, inView, playing, videoRef]);

  const [videoHasAudio, setVideoHasAudio] = useState(false);
  const onCanPlay = () => {
    if (videoRef.current) {
      setVideoHasAudio(hasAudio(videoRef.current));
    }
  };

  return {
    handleMuteToggleClick,
    handlePlayToggleClick,
    inView,
    muted,
    playing,
    videoHasAudio,
    onCanPlay,
    videoRef,
  };
};
